import React from 'react';

import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PopoverPortal } from '@radix-ui/react-popover';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { SimulateGatewayPassFormSchemaValues } from 'src/features/actions/api/useSimulateGatewayPass';
import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { REQUIRED_IDENTIFIER_TYPES } from 'src/features/gateway/utils/types';
import { actionsMessages } from 'src/features/actions/utils/messages';
import { GATEWAY_IDENTIFIER_TYPE_MAP } from 'src/features/gateway/utils/constants';
import { GeneralBlock, GeneralBlockBody } from 'src/registry/components/blocks/GeneralBlock';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import { cn } from 'src/utils/shadcn';
import { useParking } from 'src/providers/ParkingProvider';

type SimulateLPRDetectionFormProps = {
    form: UseFormReturn<SimulateGatewayPassFormSchemaValues>;
    onSubmit: () => void;
};

type FileFieldProps<T extends keyof SimulateGatewayPassFormSchemaValues> = {
    fieldName: T;
    fieldLabel: { id: string; defaultMessage: string };
};

function SimulateLPRDetectionForm({ onSubmit, form }: SimulateLPRDetectionFormProps) {
    const { formatMessage, locale, formatDate } = useIntl();
    const { parkingId } = useParking();
    const { data: gatewaysData } = useListGateways({ parkingId });

    const [uploadedFiles, setUploadedFiles] = React.useState<Record<string, File[]>>({});
    const [openCalendar, setOpenCalendar] = React.useState<boolean>(false);

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        fieldName: keyof SimulateGatewayPassFormSchemaValues,
    ) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const filesArray = Array.from(files);

        form.setValue(fieldName, filesArray, {
            shouldValidate: true,
            shouldDirty: true,
        });

        setUploadedFiles((prev) => ({
            ...prev,
            [fieldName]: filesArray,
        }));
    };

    const getFileUploadFormFieldBlock = <T extends keyof SimulateGatewayPassFormSchemaValues>({
        fieldName,
        fieldLabel,
    }: FileFieldProps<T>) => {
        return (
            <FormField
                control={form.control}
                name={fieldName}
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(fieldLabel)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <label htmlFor={fieldName} className="w-full cursor-pointer">
                                    <GeneralBlock>
                                        <GeneralBlockBody className="text-primary-700 flex w-full items-center justify-center font-bold">
                                            <div className="flex flex-col items-center">
                                                <FontAwesomeIcon
                                                    icon={faCloudArrowUp}
                                                    size="lg"
                                                    className="text-primary-700"
                                                />
                                                <span>{formatMessage(actionsMessages.clickToUpload)}</span>

                                                {uploadedFiles[fieldName]?.length > 0 && (
                                                    <div className="mt-1 flex flex-col gap-1">
                                                        {uploadedFiles[fieldName].map((file, idx) => (
                                                            <span
                                                                key={idx}
                                                                className="text-sm font-semibold text-gray-500"
                                                            >
                                                                {file.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <input
                                                type="file"
                                                accept=".jpg, .jpeg, .png, .gif, .svg"
                                                id={fieldName}
                                                className="hidden"
                                                multiple
                                                onChange={(e) => handleFileChange(e, fieldName)}
                                            />
                                        </GeneralBlockBody>
                                    </GeneralBlock>
                                </label>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        );
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="gateway"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...actionsMessages.point} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(actionsMessages.point)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {gatewaysData?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<SimulateGatewayPassFormSchemaValues>
                form={form}
                name="identifier_type"
                label={formatMessage(actionsMessages.identifierType)}
                options={Object.values(REQUIRED_IDENTIFIER_TYPES)}
                optionTranslationMap={GATEWAY_IDENTIFIER_TYPE_MAP}
            />
            <FormField
                control={form.control}
                name="identifier_value"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...actionsMessages.identifier} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input placeholder={`${formatMessage(actionsMessages.identifier)}...`} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            {getFileUploadFormFieldBlock({
                fieldName: 'files',
                fieldLabel: actionsMessages.images,
            })}

            <FormItem variant="dialog">
                <FormLabel variant="dialog">{formatMessage(commonMessages.time)}</FormLabel>

                <div className="flex w-full gap-2">
                    <div className="flex-1">
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => {
                                const selectedDate = field.value ? new Date(field.value) : undefined;
                                return (
                                    <FormItem variant="dialog">
                                        <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    id="date"
                                                    variant="outline"
                                                    className="w-full justify-start text-base font-normal"
                                                    onClick={() => setOpenCalendar(true)}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={ICON_MAP.CALENDAR}
                                                        className="text-primary-700 mr-1 size-4"
                                                    />
                                                    <span className={cn(!field.value && 'text-gray-500')}>
                                                        {field.value
                                                            ? formatDate(new Date(field.value))
                                                            : `${formatMessage(actionsMessages.selectDate)}...`}
                                                    </span>
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverPortal>
                                                <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                    <Calendar
                                                        mode="single"
                                                        selected={selectedDate}
                                                        onSelect={(date) => {
                                                            if (date) {
                                                                field.onChange(date.toISOString());
                                                                setOpenCalendar(false);
                                                            }
                                                        }}
                                                    />
                                                </PopoverContent>
                                            </PopoverPortal>
                                        </Popover>
                                    </FormItem>
                                );
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <FormField
                            control={form.control}
                            name="time"
                            render={({ field }) => (
                                <Input
                                    type="time"
                                    step={1}
                                    {...field}
                                    className="[&::-webkit-calendar-picker-indicator]:ml-12"
                                />
                            )}
                        />
                    </div>
                </div>
            </FormItem>
        </Form>
    );
}

export default SimulateLPRDetectionForm;
